import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';
import {
  ACETIC_ACID_KA,
  AMMONIA_KB,
  MAX_BURETTE_VOLUME_ML,
  type IndicatorDefinition,
  type TitrationPoint,
  type TitrationPreset,
  calculateTitrationPoint,
  equivalenceVolumeMl,
} from './titration';

/**
 * Explore view of the pH titration (CT-INTERACT, chemistry/CLAUDE.md "Interactive calculation
 * standard"). The learner types the analyte concentration and volume, the titrant concentration
 * and the titrant added. The chemistry system (strong or weak, Ka or Kb) comes from a reviewed
 * preset; every pH comes from the reviewed `calculateTitrationPoint` model in `titration.ts`.
 * All reviewed systems react in a 1 : 1 mole ratio.
 */
export interface TitrationExploreSetup {
  analyteM: number;
  analyteMl: number;
  titrantM: number;
  titrantAddedMl: number;
}

export type TitrationExploreInput = keyof TitrationExploreSetup;

export const TITRATION_EXPLORE_FIELDS: ModelField<TitrationExploreInput>[] = [
  { key: 'analyteM', label: 'Analyte concentration', unit: 'mol/L', min: 0.01, max: 1, step: 0.001 },
  { key: 'analyteMl', label: 'Analyte volume', unit: 'mL', min: 5, max: 50, step: 0.01 },
  { key: 'titrantM', label: 'Titrant concentration', unit: 'mol/L', min: 0.01, max: 1, step: 0.001 },
  { key: 'titrantAddedMl', label: 'Titrant added', unit: 'mL', min: 0, max: MAX_BURETTE_VOLUME_ML, step: 0.01 },
];

/** Kw at 25 °C, the same reviewed value as `titration.ts`. */
const KW = 1e-14;
const KW_TEXT = '1.0 × 10⁻¹⁴';
const ML_PER_L = 1000;
/** Henderson–Hasselbalch differences below this are rounding, not chemistry. */
const HH_NOTE_THRESHOLD = 0.015;

const SUPERSCRIPT: Record<string, string> = {
  '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
};

/**
 * Significant-figure formatting that keeps trailing zeros: plain between 0.001 and 10 000,
 * otherwise a × 10ⁿ. Shared by the Explore working text of the titration tools.
 */
export function formatSig(value: number, significantFigures = 3): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const magnitude = Math.abs(value);
  if (magnitude >= 1e-3 && magnitude < 1e4) {
    const plain = value.toPrecision(significantFigures);
    if (!plain.includes('e')) return plain;
  }
  const [mantissa, exponent] = value.toExponential(significantFigures - 1).split('e');
  const power = String(Number(exponent))
    .split('')
    .map((character) => SUPERSCRIPT[character] ?? character)
    .join('');
  return `${mantissa} × 10${power}`;
}

const molText = (value: number) => `${formatSig(value)} mol`;
const molarText = (value: number) => `${formatSig(value)} mol/L`;
const litreText = (ml: number) => `${(ml / ML_PER_L).toFixed(5)} L`;
const mlText = (ml: number) => `${ml.toFixed(2)} mL`;
const concentrationText = (molar: number) => `${molar.toFixed(3)} mol/L`;
const pHText = (pH: number) => pH.toFixed(2);

/** The reviewed system with the learner's numbers in place of the preset amounts. */
export function explorePreset(system: TitrationPreset, setup: TitrationExploreSetup): TitrationPreset {
  return {
    ...system,
    analyteConcentrationM: setup.analyteM,
    analyteVolumeMl: setup.analyteMl,
    titrantConcentrationM: setup.titrantM,
  };
}

/** Explore inputs that reproduce a preset, with the burette empty. */
export function setupFromPreset(preset: TitrationPreset, titrantAddedMl = 0): TitrationExploreSetup {
  return {
    analyteM: preset.analyteConcentrationM,
    analyteMl: preset.analyteVolumeMl,
    titrantM: preset.titrantConcentrationM,
    titrantAddedMl,
  };
}

export interface TitrationExploreResult {
  preset: TitrationPreset;
  point: TitrationPoint;
  equivalenceMl: number;
  analyteMol: number;
  titrantMol: number;
  totalMl: number;
  steps: WorkedStep[];
  /** True when the burette is empty (50.00 mL delivered); the model does not go further. */
  atBuretteLimit: boolean;
}

const formula = (label: string) => label.replace('(aq)', '');

function stageStatus(stage: TitrationPoint['stage'], step: 'before' | 'at' | 'after'): WorkedStep['status'] {
  const order = { 'before equivalence': 0, equivalence: 1, 'after equivalence': 2 } as const;
  const stepOrder = { before: 0, at: 1, after: 2 } as const;
  const now = order[stage];
  if (stepOrder[step] < now) return 'done';
  return stepOrder[step] === now ? 'current' : 'not-reached';
}

/** Weak acid or weak base equilibrium: x from k = x(conjugate + x) ÷ (weak − x), as in `titration.ts`. */
function weakEquilibriumX(k: number, weakM: number, conjugateM: number): number {
  const b = conjugateM + k;
  return (-b + Math.sqrt(b * b + 4 * k * weakM)) / 2;
}

function beforeWorking(
  preset: TitrationPreset,
  analyteMol: number,
  titrantMol: number,
  totalMl: number,
  modelPH: number,
): { formula: string; working: string } {
  const totalL = totalMl / ML_PER_L;
  const excess = analyteMol - titrantMol;
  const excessText = `(${molText(analyteMol)} − ${molText(titrantMol)}) ÷ ${litreText(totalMl)}`;
  if (preset.analyte === 'strong-acid') {
    const h = excess / totalL;
    return {
      formula: '[H₃O⁺] = excess n(acid) ÷ total V; pH = −log[H₃O⁺]',
      working: `[H₃O⁺] = ${excessText} = ${molarText(h)} → pH = −log(${formatSig(h)}) = ${pHText(modelPH)}`,
    };
  }
  if (preset.analyte === 'strong-base') {
    const oh = excess / totalL;
    const pOH = -Math.log10(oh);
    return {
      formula: '[OH⁻] = excess n(base) ÷ total V; pH = 14 − pOH',
      working: `[OH⁻] = ${excessText} = ${molarText(oh)} → pOH = ${pHText(pOH)} → pH = 14 − ${pHText(pOH)} = ${pHText(modelPH)}`,
    };
  }
  const isAcid = preset.analyte === 'weak-acid';
  const k = isAcid ? (preset.ka ?? ACETIC_ACID_KA) : (preset.kb ?? AMMONIA_KB);
  const kName = isAcid ? 'Ka' : 'Kb';
  const weak = isAcid ? 'HA' : 'B';
  const conjugate = isAcid ? 'A⁻' : 'BH⁺';
  if (titrantMol === 0) {
    const weakM = analyteMol / totalL;
    const x = weakEquilibriumX(k, weakM, 0);
    const ion = isAcid ? '[H₃O⁺]' : '[OH⁻]';
    const tail = isAcid
      ? `pH = −log(${formatSig(x)}) = ${pHText(modelPH)}`
      : `pOH = ${pHText(-Math.log10(x))} → pH = ${pHText(modelPH)}`;
    return {
      formula: `${kName} = x² ÷ (c − x), x = ${ion}`,
      working: `${formatSig(k, 2)} = x² ÷ (${formatSig(weakM)} − x) → x = ${molarText(x)} → ${tail}`,
    };
  }
  const pK = -Math.log10(k);
  const hh = pK + Math.log10(titrantMol / excess);
  const fullModel = isAcid ? hh : 14 - hh;
  // Henderson–Hasselbalch ignores the weak acid's own ionisation. Name the full-equilibrium pH
  // when the difference is more than rounding (near the start of the curve).
  const note = Math.abs(fullModel - modelPH) >= HH_NOTE_THRESHOLD ? ` (full equilibrium: pH ${pHText(modelPH)})` : '';
  if (isAcid) {
    return {
      formula: `pH = pKa + log(n(${conjugate}) ÷ n(${weak}))`,
      working: `pH = ${pHText(pK)} + log(${molText(titrantMol)} ÷ ${molText(excess)}) = ${pHText(hh)}${note}`,
    };
  }
  return {
    formula: `pOH = pKb + log(n(${conjugate}) ÷ n(${weak})); pH = 14 − pOH`,
    working: `pOH = ${pHText(pK)} + log(${molText(titrantMol)} ÷ ${molText(excess)}) = ${pHText(hh)} → pH = ${pHText(14 - hh)}${note}`,
  };
}

function atWorking(preset: TitrationPreset, analyteMol: number, equivalenceMl: number): { formula: string; working: string } {
  const eq = calculateTitrationPoint(preset, equivalenceMl);
  if (preset.analyte === 'strong-acid' || preset.analyte === 'strong-base') {
    return {
      formula: 'only a neutral salt and water remain (25 °C)',
      working: `n(titrant) = n(analyte) = ${molText(analyteMol)} at ${mlText(equivalenceMl)} → pH = ${pHText(eq.pH)}`,
    };
  }
  const isAcid = preset.analyte === 'weak-acid';
  const k = isAcid ? (preset.ka ?? ACETIC_ACID_KA) : (preset.kb ?? AMMONIA_KB);
  const totalMl = preset.analyteVolumeMl + equivalenceMl;
  const conjugateM = analyteMol / (totalMl / ML_PER_L);
  const ion = Math.sqrt((KW / k) * conjugateM);
  if (isAcid) {
    return {
      formula: '[OH⁻] = √(Kw ÷ Ka × c(A⁻))',
      working: `c(A⁻) = ${molText(analyteMol)} ÷ ${litreText(totalMl)} = ${molarText(conjugateM)}; [OH⁻] = √(${KW_TEXT} ÷ ${formatSig(k, 2)} × ${formatSig(conjugateM)}) = ${molarText(ion)} → pH = ${pHText(eq.pH)}`,
    };
  }
  return {
    formula: '[H₃O⁺] = √(Kw ÷ Kb × c(BH⁺))',
    working: `c(BH⁺) = ${molText(analyteMol)} ÷ ${litreText(totalMl)} = ${molarText(conjugateM)}; [H₃O⁺] = √(${KW_TEXT} ÷ ${formatSig(k, 2)} × ${formatSig(conjugateM)}) = ${molarText(ion)} → pH = ${pHText(eq.pH)}`,
  };
}

function afterWorking(
  preset: TitrationPreset,
  analyteMol: number,
  titrantMol: number,
  totalMl: number,
  modelPH: number,
): { formula: string; working: string } {
  const excess = (titrantMol - analyteMol) / (totalMl / ML_PER_L);
  const excessText = `(${molText(titrantMol)} − ${molText(analyteMol)}) ÷ ${litreText(totalMl)}`;
  const acidAnalyte = preset.analyte === 'strong-acid' || preset.analyte === 'weak-acid';
  if (acidAnalyte) {
    const pOH = -Math.log10(excess);
    return {
      formula: '[OH⁻] = excess n(titrant) ÷ total V; pH = 14 − pOH',
      working: `[OH⁻] = ${excessText} = ${molarText(excess)} → pOH = ${pHText(pOH)} → pH = ${pHText(modelPH)}`,
    };
  }
  return {
    formula: '[H₃O⁺] = excess n(titrant) ÷ total V; pH = −log[H₃O⁺]',
    working: `[H₃O⁺] = ${excessText} = ${molarText(excess)} → pH = ${pHText(modelPH)}`,
  };
}

/**
 * The learner's titration point and every worked stage. Throws on invalid input, and when the
 * equivalence point would need more than one 50.00 mL burette (fail closed, no extrapolation).
 */
export function calculateTitrationExplore(system: TitrationPreset, setup: TitrationExploreSetup): TitrationExploreResult {
  const error = firstModelError(TITRATION_EXPLORE_FIELDS, setup);
  if (error) throw new Error(error);
  const preset = explorePreset(system, setup);
  const equivalenceMl = equivalenceVolumeMl(preset);
  if (equivalenceMl > MAX_BURETTE_VOLUME_ML) {
    throw new Error(
      `Equivalence needs ${mlText(equivalenceMl)} of titrant, more than the ${mlText(MAX_BURETTE_VOLUME_ML)} burette holds. Use a more concentrated titrant or less analyte.`,
    );
  }
  const added = setup.titrantAddedMl;
  const point = calculateTitrationPoint(preset, added);
  const analyteMol = (setup.analyteM * setup.analyteMl) / ML_PER_L;
  const titrantMol = (setup.titrantM * added) / ML_PER_L;
  const totalMl = setup.analyteMl + added;
  const A = formula(preset.analyteLabel);
  const T = formula(preset.titrantLabel);

  const beforeStatus = stageStatus(point.stage, 'before');
  const before =
    beforeStatus === 'current'
      ? beforeWorking(preset, analyteMol, titrantMol, totalMl, point.pH)
      : { formula: 'excess analyte', working: `All the ${A} has reacted by ${mlText(equivalenceMl)}.` };
  const atStatus = stageStatus(point.stage, 'at');
  const at = atWorking(preset, analyteMol, equivalenceMl);
  const afterStatus = stageStatus(point.stage, 'after');
  const after =
    afterStatus === 'current'
      ? afterWorking(preset, analyteMol, titrantMol, totalMl, point.pH)
      : { formula: 'excess titrant', working: 'Not reached.' };

  const steps: WorkedStep[] = [
    {
      id: 'analyte',
      title: `Amount of ${A} in the flask`,
      formula: 'n = c × V',
      working: `n(${A}) = ${concentrationText(setup.analyteM)} × ${litreText(setup.analyteMl)} = ${molText(analyteMol)}`,
      status: 'done',
    },
    {
      id: 'equivalence',
      title: 'Equivalence volume',
      formula: 'V(eq) = n(analyte) ÷ c(titrant), 1 : 1 mole ratio',
      working: `V(eq) = ${molText(analyteMol)} ÷ ${concentrationText(setup.titrantM)} = ${litreText(equivalenceMl)} = ${mlText(equivalenceMl)}`,
      status: 'done',
    },
    {
      id: 'titrant',
      title: `Amount of ${T} added`,
      formula: 'n = c × V',
      working: `n(${T}) = ${concentrationText(setup.titrantM)} × ${litreText(added)} = ${molText(titrantMol)}`,
      status: 'done',
      note: `${mlText(added)} of ${mlText(equivalenceMl)} to equivalence`,
    },
    { id: 'before', title: 'Before equivalence', ...before, status: beforeStatus },
    { id: 'at', title: 'At equivalence', ...at, status: atStatus },
    { id: 'after', title: 'After equivalence', ...after, status: afterStatus },
  ];

  return {
    preset,
    point,
    equivalenceMl,
    analyteMol,
    titrantMol,
    totalMl,
    steps,
    atBuretteLimit: added >= MAX_BURETTE_VOLUME_ML,
  };
}

/** Where the flask colour sits in the indicator change: 0 = acid colour, 1 = base colour. */
export function indicatorShade(indicator: IndicatorDefinition, pH: number): number {
  const [low, high] = indicator.range;
  if (pH <= low) return 0;
  if (pH >= high) return 1;
  return (pH - low) / (high - low);
}

/** Learning question of the titration Explore view (CT-INTERACT C7). */
export const TITRATION_QUESTION = 'How does the pH change as you add base to an acid, and what does the equivalence point tell you?';

/** "Just before equivalence" band for the sharp-change task, mL. */
const NEAR_EQUIVALENCE_ML = 0.2;
/** Half-equivalence band for the pH = pKa task, as a fraction of the half-equivalence volume. */
const HALF_EQUIVALENCE_TOLERANCE = 0.05;
const VOLUME_EPSILON_ML = 1e-9;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the model result, never the UI. */
export const TITRATION_CHALLENGES: ExploreChallenge<TitrationExploreResult>[] = [
  {
    id: 'sharp-change',
    prompt: 'Titrate a strong acid with a strong base and stop 0.20 mL or less before equivalence. Is the pH close to 7 yet?',
    hint: 'Type a titrant volume just under the equivalence volume, or use +0.10 mL near the end.',
    isMet: ({ preset, point, equivalenceMl }) => {
      const gap = equivalenceMl - point.volumeMl;
      return preset.analyte === 'strong-acid' && point.stage === 'before equivalence' && gap > 0 && gap <= NEAR_EQUIVALENCE_ML + VOLUME_EPSILON_ML;
    },
    why: 'At 24.90 mL of 25.00 mL the pH is still only 3.70, but 0.20 mL later it is 10.30. Almost all the change happens within one or two drops, so one drop of excess turns the indicator.',
  },
  {
    id: 'weak-equivalence',
    prompt: 'Take a weak acid to its equivalence point. Does it need less base than a strong acid, and is the pH 7?',
    hint: 'Choose a weak acid system, then add titrant until V(eq) is reached.',
    isMet: ({ preset, point }) => preset.analyte === 'weak-acid' && point.stage === 'equivalence',
    why: 'It needs the same volume as a strong acid with the same amount (25.00 mL for 2.50 × 10⁻³ mol), because the base keeps removing HA as the equilibrium shifts. The pH is above 7 (8.72 for 0.100 mol/L ethanoic acid), because the ethanoate ions left behind are a weak base.',
  },
  {
    id: 'half-equivalence',
    prompt: 'With a weak acid, add half of the equivalence volume (within 5 %). Compare the pH with pKa.',
    hint: 'pKa of ethanoic acid = −log(1.8 × 10⁻⁵) = 4.74.',
    isMet: ({ preset, point, equivalenceMl }) => {
      const half = equivalenceMl / 2;
      return preset.analyte === 'weak-acid' && Math.abs(point.volumeMl - half) <= HALF_EQUIVALENCE_TOLERANCE * half;
    },
    why: 'At half-equivalence n(HA) = n(A⁻), so pH = pKa: 4.74 for ethanoic acid and about 3.74 for methanoic acid. The flask is a buffer here, so the pH changes slowly, the opposite of the jump at equivalence.',
  },
];
